/**
 * cart-scene.js — the controller for the live cart scene thumbnail.
 *
 * snippets/cart-scene-thumb.liquid draws the cart thumbnail as a scene
 * (room photograph + CSS frame + the customer's print) so a frame bought from
 * the "Complete the look" upsell is a class change rather than a re-render.
 * This file makes that markup work: it resolves which frame asset to nine-slice
 * from the print's own pixels, waits for the print while /api/cart-print-img is
 * still 404ing (the Cloudflare render takes ~10s after add-to-cart, and never
 * lands at all for lines added before that endpoint existed), and falls back to
 * the baked composite when it never arrives. Replacing the whole SCENE with the
 * composite, not just the src: the composite already contains a room and a
 * frame, so dropping it into the mount would put a scene inside a scene.
 *
 * It lives in an asset rather than inline in a section because BOTH cart
 * surfaces need it and only one of them is a cart page: sections/mini-cart.liquid
 * is the drawer, which opens on any page in the store, where main-cart-items
 * does not exist. One copy is also what stops the two surfaces drifting into
 * showing different frames for the same line.
 *
 * window._skylitSceneInit() is idempotent (each scene carries a data-scene-init
 * flag) and safe to call after any Section Rendering swap — a swap produces new
 * nodes, so the flag comes back unset and they initialise.
 */
(function () {
  window._skylitSceneInit = function () {
    var scenes = document.querySelectorAll('.skylit-scene[data-print-url]');
    Array.prototype.forEach.call(scenes, function (scene) {
      if (scene.dataset.sceneInit === '1') return;   // survives section swaps: new node, new flag
      scene.dataset.sceneInit = '1';

      var img = scene.querySelector('.skylit-scene__print');
      var printUrl = scene.getAttribute('data-print-url');
      var compositeUrl = scene.getAttribute('data-composite-url');
      if (!img || !printUrl) return;

      // Which frame asset to nine-slice. Taken from the print itself rather
      // than the line's properties: Your Name From Orbit is always landscape,
      // family moon depends on its composition, location maps on the size
      // ordered — the pixels are the one source that knows for all of them.
      function setOrientation() {
        if (!img.naturalWidth || !img.naturalHeight) return;
        scene.setAttribute('data-orientation',
          img.naturalWidth > img.naturalHeight ? 'landscape' : 'portrait');
        // Real pixels: drop the reserved placeholder box and let the print
        // size the mount itself again.
        scene.removeAttribute('data-print-state');
      }
      img.addEventListener('load', setOrientation);
      setOrientation();   // cached image: already decoded, no load event coming

      if (img.complete && img.naturalWidth > 0) return;   // already loaded

      // No pixels yet — hold the print's height so the mount keeps its shape
      // instead of collapsing to a bar. Set before the loader goes up, since
      // in the preview modal the loader is positioned against this box.
      scene.setAttribute('data-print-state', 'pending');

      var loader = null;
      function showLoader() {
        try {
          // The cart cell is ~100px wide, so there the loader takes the whole
          // scene — the spinner and its label would not fit inside the print.
          // The preview modal is big enough to keep the room and the frame on
          // screen and put the loader in the artwork instead, which is also
          // the only place it can go without covering the frame the customer
          // is opening the preview to look at.
          var host = scene.getAttribute('data-loader-target') === 'mount'
            ? (scene.querySelector('.skylit-scene__mount') || scene)
            : scene;
          if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
          loader = document.createElement('div');
          loader.className = 'cart-thumb-generating';
          loader.setAttribute('aria-label', 'Generating preview');
          loader.style.cssText = 'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:7px;padding:6px;box-sizing:border-box;background:#efe9df;color:#6a5a44;font-size:9px;line-height:1.3;letter-spacing:.02em;text-transform:uppercase;z-index:2;';
          loader.innerHTML = '<span style="width:18px;height:18px;border:2px solid rgba(120,90,50,.25);border-top-color:#8b6914;border-radius:50%;display:inline-block;animation:cartThumbSpin .8s linear infinite;"></span><span class="cart-thumb-generating__label" style="display:block;max-width:100%;overflow-wrap:break-word;">Generating<br>preview…</span>';
          // The keyframes live with the other loader (the legacy composite
          // upgrade path) and that one may never run on a scene cart, so
          // define them here too rather than depend on the ordering.
          if (!document.getElementById('cart-thumb-gen-kf')) {
            var st = document.createElement('style');
            st.id = 'cart-thumb-gen-kf';
            st.textContent = '@keyframes cartThumbSpin{to{transform:rotate(360deg)}}';
            document.head.appendChild(st);
          }
          host.appendChild(loader);
          try { if (host.clientWidth && host.clientWidth < 84) loader.querySelector('.cart-thumb-generating__label').style.display = 'none'; } catch (e) {}
          // Same 20s cue as the gallery wall builder: a slow render should
          // read as in-hand, not as stuck.
          setTimeout(markStillWorking, 20000);
        } catch (e) {}
      }
      function hideLoader() { try { if (loader && loader.parentNode) loader.parentNode.removeChild(loader); } catch (e) {} }

      // Cache-buster on every probe and every swap: these URLs are stable per
      // order but their CONTENT changes on every edit, and a cached copy would
      // show the previous design.
      function bust(url) {
        return url + (url.indexOf('?') > -1 ? '&' : '?') + 'v=' + Date.now();
      }

      // Swap the whole scene for the baked composite — room, frame and print
      // in one flat image. Only ever called with an image that has ALREADY
      // loaded: it used to be set blind, and on a re-edit that is a broken
      // picture, because the trigger nulls cart_thumbnail_r2_key at the same
      // time as cart_print_r2_key, so /api/cart-thumbnail-img 404s exactly as
      // often as the print does. Nor could the legacy localStorage loader
      // rescue it — that selects .cart-item__image[data-order-id], and this
      // element deliberately has no data-order-id.
      function swapInComposite(src) {
        hideLoader();
        var flat = document.createElement('img');
        flat.className = 'cart-item__image';
        flat.src = src;
        flat.alt = img.getAttribute('alt') || '';
        flat.setAttribute('data-product-title', img.getAttribute('data-product-title') || '');
        flat.setAttribute('data-preview-click', 'true');
        // 'done' so upgradeCartBackendThumbnails leaves it alone — it is
        // already the final image, and a second poll would restart the loader.
        flat.dataset.thumbUpgrade = 'done';
        if (scene.parentNode) scene.parentNode.replaceChild(flat, scene);
      }

      // Probe the composite; hand back whether it is actually there. Legacy
      // orders have one and no bare print, so this is a real recovery — it
      // just must not fire when the answer is a 404.
      function tryComposite(done) {
        if (!compositeUrl) { done(false); return; }
        var src = bust(compositeUrl);
        var probe = new Image();
        probe.onload = function () { swapInComposite(src); done(true); };
        probe.onerror = function () { done(false); };
        probe.src = src;
      }

      // Two phases. The first ~69s is the impatient one, for the ordinary
      // 8-11s render. Past that the render was probably cancelled mid-flight
      // (it runs in the save's ctx.waitUntil, which Cloudflare kills ~30s
      // after that request ends) and the every-3-minutes cron sweep is what
      // will land it — which is LONGER than the fast phase, so giving up here
      // meant the customer never saw their own preview without reloading.
      // Keep the loader and keep checking, just quietly.
      var FAST_MAX = 40;                  // 9×1s + 30×2s ≈ 69s
      var SLOW_EVERY_MS = 10000;
      var SLOW_CEILING_MS = 10 * 60 * 1000;   // cron lookback is 2h; 10min is generous
      var attempts = 0;
      var startedAt = Date.now();

      function poll() {
        // The preview modal clears its clone on close; stop polling for a
        // scene that is no longer on the page.
        if (!scene.isConnected) return;
        attempts++;
        var probe = new Image();
        probe.onload = function () {
          img.src = bust(printUrl);
          hideLoader();
        };
        probe.onerror = function () {
          if (attempts < FAST_MAX) {
            setTimeout(poll, attempts < 10 ? 1000 : 2000);
          } else if (attempts === FAST_MAX) {
            tryComposite(function (swapped) {
              if (swapped) return;
              markStillWorking();
              setTimeout(poll, SLOW_EVERY_MS);
            });
          } else if (Date.now() - startedAt < SLOW_CEILING_MS) {
            setTimeout(poll, SLOW_EVERY_MS);
          } else {
            // Ten minutes is past anything the sweep explains. Stop the
            // spinner rather than leave one turning forever, and say what is
            // true — nothing here is wrong with their order, and the line
            // still carries every detail of it.
            tryComposite(function (swapped) { if (!swapped) markStalled(); });
          }
        };
        probe.src = bust(printUrl);
      }

      function loaderLabel() {
        return loader && loader.lastElementChild;
      }
      function markStillWorking() {
        var el = loaderLabel();
        if (el && loader && loader.parentNode) el.textContent = 'Bear with us, this one is taking a little longer…';
      }
      function markStalled() {
        var spinner = loader && loader.firstElementChild;
        if (spinner) spinner.style.display = 'none';
        var el = loaderLabel();
        if (el) el.textContent = 'Preview still generating';
      }

      img.addEventListener('error', function () {
        if (scene.dataset.scenePolling === '1') return;
        scene.dataset.scenePolling = '1';
        showLoader();
        poll();
      });
      // The error may already have fired before this ran (cached 404).
      if (img.complete && img.naturalWidth === 0) img.dispatchEvent(new Event('error'));
    });
  };
  window._skylitSceneInit();

  // ── Re-init after a re-render ────────────────────────────────────────────
  // Every cart mutation re-renders this markup through Section Rendering, and
  // an innerHTML swap neither re-runs scripts nor preserves the data-scene-init
  // flags — the nodes are new. The cart page has its own MutationObserver that
  // calls back in; the drawer has nothing, and it is the surface that gets
  // swapped most (it re-renders on every add from anywhere in the store).
  //
  // Cheap to over-call: initialised scenes carry the flag and are skipped.
  function reinit() {
    try { window._skylitSceneInit(); } catch (e) {}
  }
  ['cart:updated', 'cart:refresh', 'cartdrawer:opened', 'ajaxProduct:added'].forEach(function (evt) {
    document.addEventListener(evt, function () { setTimeout(reinit, 0); });
  });

  // The drawer is also populated without any of those events firing — the lazy
  // first open fetches and injects it directly. Watch the container instead of
  // guessing which path filled it.
  function watchMiniCart() {
    var root = document.getElementById('mini-cart');
    if (!root || root.dataset.sceneObserved === '1' || !window.MutationObserver) return;
    root.dataset.sceneObserved = '1';
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(reinit, 60);
    }).observe(root, { childList: true, subtree: true });
  }
  watchMiniCart();
  document.addEventListener('cartdrawer:opened', watchMiniCart);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { watchMiniCart(); reinit(); });
  }
})();
